import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    BarChart, Bar, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip,
    ResponsiveContainer, Legend,
} from 'recharts';
import { format as fmtDateFns, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
    DollarSign, Send, MousePointerClick,
    Calendar, CheckCircle2, AlertCircle, ChevronDown, ChevronUp,
    Loader2,
} from 'lucide-react';
import {
    getMetaDashboard,
    getRevenueBatch,
    getDomainHealth,
    getFunnelsDashboard,
    getFunnelRevenueBatch,
    BroadcastResult,
    DomainHealth,
    FunnelResult,
} from '@/services/api/meta-analytics';
import { getPageCategories } from '@/services/api/connections';
import { GeoRevenueMatrix } from './GeoRevenueMatrix';
import { FunnelGeoRevenueMatrix } from './FunnelGeoRevenueMatrix';
import { FunnelPanel } from './FunnelPanel';
import { GroupPanel } from './GroupPanel';
import { DomainPanel } from './DomainPanel';
import { format, subDays } from 'date-fns';
import {
    fmtMoney, fmtN, fmtPct,
    STATUS_LABEL, STATUS_COLOR,
} from './utils';

// ── Skeleton ──────────────────────────────────────────────────────────────────

function Skeleton({ className = '' }: { className?: string }) {
    return <div className={`animate-pulse bg-gray-100 rounded ${className}`} />;
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, sub, color, loading, warn }: {
    icon: React.ReactNode;
    label: string;
    value?: string;
    sub?: string;
    color: string;
    loading?: boolean;
    warn?: boolean;
}) {
    return (
        <div className={`rounded-2xl bg-white border shadow-sm p-5 flex items-start gap-4 ${warn ? 'border-red-200' : 'border-gray-100'}`}>
            <div className={`p-2.5 rounded-xl ${color}`}>{icon}</div>
            <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 mb-0.5">{label}</p>
                {loading ? <Skeleton className="h-8 w-24" /> : (
                    <>
                        <p className={`text-2xl font-bold ${warn ? 'text-red-600' : 'text-gray-900'}`}>
                            {value ?? '—'}
                        </p>
                        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
                    </>
                )}
            </div>
        </div>
    );
}

// ── DateRangePicker ───────────────────────────────────────────────────────────

interface DatePreset { label: string; days: number }

const DATE_PRESETS: DatePreset[] = [
    { label: 'Hoje', days: 0 },
    { label: '7d',   days: 6 },
    { label: '14d',  days: 13 },
    { label: '30d',  days: 29 },
];

function DateRangePicker({ start, end, setStart, setEnd }: {
    start: string; end: string;
    setStart: (v: string) => void; setEnd: (v: string) => void;
}) {
    const today = format(new Date(), 'yyyy-MM-dd');
    const activePreset = DATE_PRESETS.find(p => {
        const ps = format(subDays(new Date(), p.days), 'yyyy-MM-dd');
        return ps === start && end === today;
    });

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
            <div className="flex items-center gap-1">
                {DATE_PRESETS.map(p => {
                    const isActive = activePreset?.label === p.label;
                    return (
                        <button
                            key={p.label}
                            onClick={() => {
                                setStart(format(subDays(new Date(), p.days), 'yyyy-MM-dd'));
                                setEnd(today);
                            }}
                            className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                                isActive
                                    ? 'bg-gray-900 text-white border-gray-900'
                                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                            {p.label}
                        </button>
                    );
                })}
            </div>
            <input
                type="date"
                value={start}
                max={end}
                onChange={e => setStart(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-gray-900/20"
            />
            <span className="text-gray-400 text-sm">→</span>
            <input
                type="date"
                value={end}
                min={start}
                max={today}
                onChange={e => setEnd(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-gray-900/20"
            />
        </div>
    );
}

// ── GlobalFunnel ──────────────────────────────────────────────────────────────

function GlobalFunnel({ sent, delivered, clicks, revenue, revenueLoading }: {
    sent: number;
    delivered: number;
    clicks: number;
    revenue: number;
    revenueLoading: boolean;
}) {
    const deliveryRate = sent > 0 ? fmtPct((delivered / sent) * 100, 1) : '—';
    const ctr          = sent > 0 ? fmtPct((clicks / sent) * 100, 3) : '—';
    const rpm          = sent > 0 ? fmtMoney(revenue / sent * 1000) : '—';
    const rpc          = clicks > 0 ? fmtMoney(revenue / clicks) : '—';

    return (
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Broad Global</h2>

            <div className="flex items-center gap-2 flex-wrap">
                {/* Enviados */}
                <div className="flex-1 min-w-[110px] text-center">
                    <p className="text-2xl font-bold text-blue-600">{sent.toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Enviados</p>
                    <p className="text-xs text-blue-400 mt-0.5">100%</p>
                </div>
                <div className="text-gray-300 font-bold text-lg shrink-0">→</div>

                {/* Entregues */}
                <div className="flex-1 min-w-[110px] text-center">
                    <p className="text-2xl font-bold text-cyan-600">{delivered.toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Entregues</p>
                    <p className="text-xs text-cyan-400 mt-0.5">{deliveryRate}</p>
                </div>
                <div className="text-gray-300 font-bold text-lg shrink-0">→</div>

                {/* Cliques */}
                <div className="flex-1 min-w-[110px] text-center">
                    <p className="text-2xl font-bold text-indigo-600">{clicks.toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Cliques</p>
                    <p className="text-xs text-indigo-400 mt-0.5">{ctr}</p>
                </div>
                <div className="text-gray-300 font-bold text-lg shrink-0">→</div>

                {/* Revenue */}
                <div className="flex-1 min-w-[110px] text-center">
                    {revenueLoading
                        ? <Skeleton className="h-8 w-24 mx-auto" />
                        : <p className="text-2xl font-bold text-emerald-600">{fmtMoney(revenue)}</p>
                    }
                    <p className="text-xs text-gray-400 mt-0.5">Revenue</p>
                </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-6 flex-wrap text-sm">
                <div><span className="text-gray-400">RPM: </span>
                    {revenueLoading ? <Skeleton className="h-4 w-16 inline-block" /> : <span className="font-semibold text-gray-700">{rpm}</span>}
                </div>
                <div><span className="text-gray-400">RPC: </span>
                    {revenueLoading ? <Skeleton className="h-4 w-16 inline-block" /> : <span className="font-semibold text-gray-700">{rpc}</span>}
                </div>
            </div>
        </div>
    );
}

// ── DomainsPanel ──────────────────────────────────────────────────────────────

function DomainsList({ domains, loading, onSelect }: {
    domains: DomainHealth[];
    loading: boolean;
    onSelect: (domain: string) => void;
}) {
    const sorted = useMemo(
        () => [...domains].sort((a, b) => {
            if (a.has_revenue && !b.has_revenue) return -1;
            if (!a.has_revenue && b.has_revenue) return 1;
            return b.revenue - a.revenue;
        }),
        [domains]
    );

    return (
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <h2 className="text-sm font-semibold text-gray-800">Domains</h2>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
                    {domains.length}
                </span>
            </div>

            {loading ? (
                <div className="p-4 space-y-2">
                    {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12" />)}
                </div>
            ) : sorted.length === 0 ? (
                <p className="text-sm text-gray-400 p-5">Sem dados de domain no período.</p>
            ) : (
                <div className="divide-y divide-gray-100 overflow-y-auto flex-1">
                    {sorted.map(d => (
                        <button
                            key={d.domain}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                            onClick={() => onSelect(d.domain)}
                        >
                            {d.has_revenue ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                            ) : (
                                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${d.has_revenue ? 'text-gray-800' : 'text-gray-400'}`}>
                                    {d.domain}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    {d.total_pages}p · {fmtN(d.total_sent)} env
                                </p>
                            </div>
                            {d.has_revenue ? (
                                <div className="text-right shrink-0">
                                    <p className="text-sm font-semibold text-emerald-700">{fmtMoney(d.revenue)}</p>
                                    <p className="text-xs text-gray-400">{fmtMoney(d.ecpm)} eCPM</p>
                                </div>
                            ) : (
                                <span className="text-xs text-red-400 shrink-0">Sem revenue</span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── TopGroups ─────────────────────────────────────────────────────────────────

type GroupSortKey = 'sent' | 'clicks' | 'revenue' | 'recent';

function TopGroups({ groups, revenueMap, revenueLoadingMap, onSelect }: {
    groups: BroadcastResult[];
    revenueMap: Record<number, number | undefined>;
    revenueLoadingMap: Record<number, boolean>;
    onSelect: (g: BroadcastResult) => void;
}) {
    const [sortBy, setSortBy] = useState<GroupSortKey>('sent');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    const sorted = useMemo(() => {
        if (groups.length === 0) return [];

        const ts = (g: BroadcastResult) => g.created_at ? new Date(g.created_at).getTime() : 0;

        // grupo mais recente entre TODOS os groups
        const mostRecent = groups.reduce((acc, g) => ts(g) > ts(acc) ? g : acc, groups[0]);

        const rest = groups
            .filter(g => g.id !== mostRecent.id)
            .sort((a, b) => {
                let va = 0, vb = 0;
                if (sortBy === 'sent')    { va = a.total_sent; vb = b.total_sent; }
                if (sortBy === 'clicks')  { va = a.total_clicks; vb = b.total_clicks; }
                if (sortBy === 'revenue') { va = revenueMap[a.id] ?? 0; vb = revenueMap[b.id] ?? 0; }
                if (sortBy === 'recent')  { va = ts(a); vb = ts(b); }
                return sortDir === 'desc' ? vb - va : va - vb;
            });

        return [mostRecent, ...rest].slice(0, 10);
    }, [groups, sortBy, sortDir, revenueMap]);

    const toggle = (k: GroupSortKey) => {
        if (sortBy === k) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        else { setSortBy(k); setSortDir('desc'); }
    };

    const SortBtn = ({ k, label }: { k: GroupSortKey; label: string }) => (
        <button
            onClick={() => toggle(k)}
            className={`flex items-center gap-0.5 text-xs px-2 py-1 rounded-md border transition-colors ${
                sortBy === k
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
            }`}
        >
            {label}
            {sortBy === k && (sortDir === 'desc'
                ? <ChevronDown className="w-3 h-3" />
                : <ChevronUp className="w-3 h-3" />
            )}
        </button>
    );

    return (
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-semibold text-gray-800 flex-1">Top Grupos</h2>
                <SortBtn k="sent" label="Enviados" />
                <SortBtn k="clicks" label="Cliques" />
                <SortBtn k="revenue" label="Revenue" />
                <SortBtn k="recent" label="Recentes" />
            </div>

            {sorted.length === 0 ? (
                <p className="text-sm text-gray-400 p-5">Sem grupos no período.</p>
            ) : (
                <div className="divide-y divide-gray-100 overflow-y-auto flex-1">
                    {sorted.map((g, i) => {
                        const rev = revenueMap[g.id];
                        const revLoading = revenueLoadingMap[g.id] ?? false;
                        const statusCls = STATUS_COLOR[g.status] ?? 'bg-gray-100 text-gray-500';
                        const failRate = g.total_sent > 0
                            ? (1 - g.delivery_rate / 100) * 100
                            : 0;
                        return (
                            <button
                                key={g.id}
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50/30 transition-colors text-left"
                                onClick={() => onSelect(g)}
                            >
                                <span className="text-xs font-bold text-gray-300 w-5 shrink-0">#{i + 1}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate" title={g.title}>
                                        {g.title}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        {fmtN(g.total_sent)} env ·{' '}
                                        <span className={failRate > 50 ? 'text-red-500' : 'text-gray-400'}>
                                            {fmtPct(failRate)} fail
                                        </span>
                                        {' '}· {fmtPct(g.ctr, 2)} CTR
                                    </p>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${statusCls}`}>
                                    {STATUS_LABEL[g.status] ?? g.status}
                                </span>
                                {revLoading ? (
                                    <Skeleton className="h-4 w-16 shrink-0" />
                                ) : rev !== undefined ? (
                                    <span className="text-sm font-semibold text-emerald-700 shrink-0">
                                        {fmtMoney(rev)}
                                    </span>
                                ) : (
                                    <span className="text-xs text-gray-300 shrink-0">—</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Gráficos de cliques ───────────────────────────────────────────────────────

function ChartTooltipContent({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-xl border border-gray-200 bg-white/95 px-3 py-2 shadow-lg text-sm">
            <p className="text-xs text-gray-400 mb-1">{label}</p>
            {payload.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                    <span className="text-gray-600">{p.name}:</span>
                    <span className="font-semibold text-gray-900">{fmtN(p.value)}</span>
                </div>
            ))}
        </div>
    );
}

function DailyClicksChart({ data, color = '#6366f1' }: { data: { date: string; clicks: number; unique_leads: number }[]; color?: string }) {
    const chartData = data.map(d => ({
        ...d,
        date_fmt: (() => { try { return fmtDateFns(parseISO(d.date), 'dd/MM', { locale: ptBR }); } catch { return d.date; } })(),
    }));
    return (
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-800 mb-4">Cliques por dia</h2>
            <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date_fmt" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} width={48} tickFormatter={v => fmtN(v as number)} />
                    <RechartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Line type="monotone" dataKey="clicks" name="Cliques" stroke={color} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="unique_leads" name="Clicadores únicos" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 2" />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function HourlyClicksChart({ data, color = '#6366f1' }: { data: { hour: number; clicks: number; unique_leads: number }[]; color?: string }) {
    const full = useMemo(() => {
        const map = Object.fromEntries(data.map(d => [d.hour, d]));
        return Array.from({ length: 24 }, (_, h) => ({
            hour: h,
            label: `${String(h).padStart(2, '0')}h`,
            clicks: map[h]?.clicks ?? 0,
            unique_leads: map[h]?.unique_leads ?? 0,
        }));
    }, [data]);
    return (
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-800 mb-4">Cliques por hora</h2>
            <ResponsiveContainer width="100%" height={160}>
                <BarChart data={full} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                    <YAxis tick={{ fontSize: 11 }} width={40} tickFormatter={v => fmtN(v as number)} />
                    <RechartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="clicks" name="Cliques" fill={color} radius={[2, 2, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

// ── FunnelsTab ────────────────────────────────────────────────────────────────

function FunnelsTab({ funnels, revenueMap, revenueLoading, isLoading, onSelect }: {
    funnels: FunnelResult[];
    revenueMap: Record<number, number>;
    revenueLoading: boolean;
    isLoading: boolean;
    onSelect: (f: FunnelResult) => void;
}) {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Carregando funnels...</span>
            </div>
        );
    }
    if (!funnels.length) {
        return (
            <div className="rounded-2xl bg-white border border-gray-100 p-12 text-center">
                <p className="text-gray-400 text-sm">Nenhum funnel com cliques no período.</p>
            </div>
        );
    }
    const sorted = [...funnels].sort((a, b) => b.total_clicks - a.total_clicks);
    return (
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Funnel</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliques</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Clicadores únicos</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Revenue</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">RPM cliques</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                    {sorted.map(f => {
                        const rev = revenueMap[f.id];
                        const rpm = rev != null && f.total_clicks > 0 ? rev / f.total_clicks * 1000 : null;
                        return (
                            <tr key={f.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => onSelect(f)}>
                                <td className="px-4 py-3">
                                    <p className="font-medium text-gray-900 truncate max-w-xs">{f.title}</p>
                                    <p className="text-[11px] text-gray-400 font-mono">flow_{f.id}</p>
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-indigo-600">{f.total_clicks.toLocaleString('pt-BR')}</td>
                                <td className="px-4 py-3 text-right text-gray-600">{f.unique_leads.toLocaleString('pt-BR')}</td>
                                <td className="px-4 py-3 text-right">
                                    {revenueLoading
                                        ? <span className="inline-block animate-pulse bg-gray-100 rounded h-4 w-16" />
                                        : rev != null
                                            ? <span className={`font-semibold ${rev > 0 ? 'text-emerald-600' : 'text-red-400'}`}>{fmtMoney(rev)}</span>
                                            : <span className="text-red-400 text-xs">sem revenue</span>
                                    }
                                </td>
                                <td className="px-4 py-3 text-right text-gray-500 text-xs">
                                    {revenueLoading
                                        ? <span className="inline-block animate-pulse bg-gray-100 rounded h-4 w-12" />
                                        : rpm != null ? `$${rpm.toFixed(2)}` : '–'
                                    }
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ── Página principal ──────────────────────────────────────────────────────────

type ActiveTab = 'broadcasts' | 'funnels';

export default function MetaAnalyticsV2() {
    const today = format(new Date(), 'yyyy-MM-dd');
    const [start, setStart] = useState(today);
    const [end, setEnd] = useState(today);
    const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
    const [activeTab, setActiveTab] = useState<ActiveTab>('broadcasts');

    // painéis
    const [selectedGroup, setSelectedGroup] = useState<BroadcastResult | null>(null);
    const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
    const [selectedFunnel, setSelectedFunnel] = useState<FunnelResult | null>(null);

    // ── queries principais ────────────────────────────────────────────────────

    const categoriesQuery = useQuery({
        queryKey: ['page-categories'],
        queryFn: () => getPageCategories(),
        staleTime: 5 * 60 * 1000,
    });

    const dashQuery = useQuery({
        queryKey: ['meta-analytics-v2-dashboard', start, end, categoryId],
        queryFn: () => getMetaDashboard(start, end, categoryId),
        staleTime: 1000 * 60 * 2,
        refetchOnWindowFocus: false,
    });

    const domainHealthQuery = useQuery({
        queryKey: ['meta-analytics-v2-domain-health', start, end, categoryId],
        queryFn: () => getDomainHealth(start, end, categoryId),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
    });

    const dash = dashQuery.data?.data;
    const groups = dash?.results ?? [];

    // ── queries funnels ───────────────────────────────────────────────────────

    const funnelDashQuery = useQuery({
        queryKey: ['meta-analytics-v2-funnels', start, end, categoryId],
        queryFn: () => getFunnelsDashboard(start, end, categoryId),
        staleTime: 1000 * 60 * 2,
        refetchOnWindowFocus: false,
        enabled: activeTab === 'funnels',
    });

    const funnels: FunnelResult[] = funnelDashQuery.data?.data?.results ?? [];

    const funnelRevenueQuery = useQuery({
        queryKey: ['meta-analytics-v2-funnel-revenue-batch', start, end, categoryId],
        queryFn: () => getFunnelRevenueBatch(start, end, undefined, categoryId),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        enabled: activeTab === 'funnels' && funnels.length > 0,
    });

    const funnelRevenueMap = useMemo(() => {
        const m: Record<number, number> = {};
        (funnelRevenueQuery.data?.data ?? []).forEach(item => {
            m[item.funnel_id] = item.total_revenue;
        });
        return m;
    }, [funnelRevenueQuery.data]);

    const globalFunnelRevenue = useMemo(() =>
        Object.values(funnelRevenueMap).reduce((s, v) => s + v, 0),
        [funnelRevenueMap]
    );
    const funnelRevenueLoading = funnelRevenueQuery.isLoading || funnelRevenueQuery.isFetching;

    // ── revenue batch — 1 req para todos os grupos ───────────────────────────

    const revenueBatchQuery = useQuery({
        queryKey: ['broadcast-revenue-v2-batch', start, end, categoryId],
        queryFn: () => getRevenueBatch(start, end, undefined, categoryId),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        retry: 1,
        enabled: groups.length > 0,
    });

    // ── maps derivados ────────────────────────────────────────────────────────

    const revenueMap = useMemo(() => {
        const m: Record<number, number | undefined> = {};
        const batchData = revenueBatchQuery.data?.data;
        if (batchData) {
            for (const item of batchData) {
                m[item.group_id] = item.total_revenue;
            }
        }
        return m;
    }, [revenueBatchQuery.data]);

    const revenueLoadingMap = useMemo(() => {
        const loading = revenueBatchQuery.isLoading || revenueBatchQuery.isFetching;
        const m: Record<number, boolean> = {};
        groups.forEach(g => { m[g.id] = loading; });
        return m;
    }, [groups, revenueBatchQuery.isLoading, revenueBatchQuery.isFetching]);

    const domainHealthMap = useMemo(() => {
        const m: Record<string, DomainHealth> = {};
        domainHealthQuery.data?.data?.forEach(d => { m[d.domain] = d; });
        return m;
    }, [domainHealthQuery.data]);

    // revenue total global
    const { globalRevenue, globalRevenueLoading } = useMemo(() => {
        const loading = revenueBatchQuery.isLoading || revenueBatchQuery.isFetching;
        const total = (revenueBatchQuery.data?.data ?? []).reduce((s, item) => s + item.total_revenue, 0);
        return { globalRevenue: total, globalRevenueLoading: loading };
    }, [revenueBatchQuery.data, revenueBatchQuery.isLoading, revenueBatchQuery.isFetching]);


    // map: domain → { revenue por grupo } — do batch
    const domainGroupRevenueMap = useMemo(() => {
        const m: Record<string, Record<number, number | undefined>> = {};
        (revenueBatchQuery.data?.data ?? []).forEach(item => {
            item.by_domain.forEach(d => {
                if (!m[d.domain]) m[d.domain] = {};
                m[d.domain][item.group_id] = d.revenue;
            });
        });
        return m;
    }, [revenueBatchQuery.data]);

    // grupos que têm o domain selecionado
    const domainGroupRevMap = useMemo(() => {
        if (!selectedDomain) return {};
        return domainGroupRevenueMap[selectedDomain] ?? {};
    }, [selectedDomain, domainGroupRevenueMap]);

    // ── handlers ──────────────────────────────────────────────────────────────

    const handleOpenGroup = (g: BroadcastResult) => {
        setSelectedGroup(g);
    };

    const handleOpenDomain = (domain: string) => {
        setSelectedDomain(domain);
    };

    const handleCloseGroup = () => setSelectedGroup(null);
    const handleCloseDomain = () => setSelectedDomain(null);

    const handleOpenGroupFromDomain = (groupId: number, groupTitle: string, groupStatus: string) => {
        setSelectedDomain(null);
        const g = groups.find(gr => gr.id === groupId);
        if (g) {
            setSelectedGroup(g);
        } else {
            setSelectedGroup({ id: groupId, title: groupTitle, status: groupStatus } as BroadcastResult);
        }
    };

    const handleOpenDomainFromGroup = (domain: string) => {
        setSelectedDomain(domain);
    };

    // ── render ────────────────────────────────────────────────────────────────

    const totalSent = dash?.summary.total_sent ?? 0;
    const totalClicks = dash?.summary.total_clicks ?? 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
                </div>
                <DateRangePicker start={start} end={end} setStart={setStart} setEnd={setEnd} />
                <select
                    value={categoryId ?? ''}
                    onChange={e => setCategoryId(e.target.value ? Number(e.target.value) : undefined)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">Todas as categorias</option>
                    {categoriesQuery.data?.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
            </div>

            {/* Abas */}
            <div className="flex gap-1 border-b border-gray-200">
                {(['broadcasts', 'funnels'] as ActiveTab[]).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px capitalize ${
                            activeTab === tab
                                ? 'border-gray-900 text-gray-900'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        {tab === 'broadcasts' ? 'Broadcasts' : 'Funnels'}
                    </button>
                ))}
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                    icon={<DollarSign className="w-5 h-5 text-green-600" />}
                    label={activeTab === 'broadcasts' ? 'Revenue Broadcasts' : 'Revenue Funnels'}
                    value={activeTab === 'broadcasts'
                        ? (globalRevenueLoading ? undefined : fmtMoney(globalRevenue))
                        : (funnelRevenueLoading ? undefined : fmtMoney(globalFunnelRevenue))
                    }
                    loading={activeTab === 'broadcasts' ? globalRevenueLoading : funnelRevenueLoading}
                    color="bg-green-50"
                />
                {activeTab === 'broadcasts' ? <>
                    <KpiCard
                        icon={<Send className="w-5 h-5 text-blue-600" />}
                        label="Enviados"
                        value={dash ? totalSent.toLocaleString('pt-BR') : undefined}
                        loading={dashQuery.isLoading}
                        color="bg-blue-50"
                        sub={dash ? `${dash.summary.total_broadcasts} grupos` : undefined}
                    />
                    <KpiCard
                        icon={<MousePointerClick className="w-5 h-5 text-indigo-600" />}
                        label="Cliques únicos"
                        value={dash ? totalClicks.toLocaleString('pt-BR') : undefined}
                        loading={dashQuery.isLoading}
                        color="bg-indigo-50"
                        sub={dash && totalSent > 0 ? `CTR ${fmtPct((totalClicks / totalSent) * 100, 3)}` : undefined}
                    />
                    <KpiCard
                        icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                        label="Delivery"
                        value={dash ? fmtPct(dash.summary.delivery_rate) : undefined}
                        loading={dashQuery.isLoading}
                        color="bg-emerald-50"
                        sub={dash ? `${dash.summary.total_delivered.toLocaleString('pt-BR')} entregues` : undefined}
                    />
                </> : <>
                    <KpiCard
                        icon={<Send className="w-5 h-5 text-teal-600" />}
                        label="Funnels ativos"
                        value={funnelDashQuery.data?.data ? String(funnelDashQuery.data.data.summary.total_funnels) : undefined}
                        loading={funnelDashQuery.isLoading}
                        color="bg-teal-50"
                        sub="com cliques no período"
                    />
                    <KpiCard
                        icon={<MousePointerClick className="w-5 h-5 text-indigo-600" />}
                        label="Cliques totais"
                        value={funnelDashQuery.data?.data ? funnelDashQuery.data.data.summary.total_clicks.toLocaleString('pt-BR') : undefined}
                        loading={funnelDashQuery.isLoading}
                        color="bg-indigo-50"
                    />
                    <KpiCard
                        icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                        label="Clicadores únicos"
                        value={funnelDashQuery.data?.data ? funnelDashQuery.data.data.summary.total_unique_leads.toLocaleString('pt-BR') : undefined}
                        loading={funnelDashQuery.isLoading}
                        color="bg-emerald-50"
                    />
                </>}
            </div>

            {/* ── Aba Broadcasts ── */}
            {activeTab === 'broadcasts' && <>
                {(dash || dashQuery.isLoading) && (
                    <GlobalFunnel
                        sent={totalSent}
                        delivered={dash?.summary.total_delivered ?? 0}
                        clicks={totalClicks}
                        revenue={globalRevenue}
                        revenueLoading={globalRevenueLoading}
                    />
                )}
                {(dash?.daily_clicks ?? []).length > 0 && (
                    <DailyClicksChart data={dash!.daily_clicks} />
                )}
                {(dash?.hourly_clicks ?? []).length > 0 && (
                    <HourlyClicksChart data={dash!.hourly_clicks} />
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[400px]">
                    <DomainsList
                        domains={domainHealthQuery.data?.data ?? []}
                        loading={domainHealthQuery.isLoading}
                        onSelect={handleOpenDomain}
                    />
                    <TopGroups
                        groups={groups}
                        revenueMap={revenueMap}
                        revenueLoadingMap={revenueLoadingMap}
                        onSelect={handleOpenGroup}
                    />
                </div>
                <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-6">
                    <div className="mb-4">
                        <h2 className="text-base font-semibold text-gray-900">País × Domain</h2>
                        <p className="text-xs text-gray-400 mt-0.5">Revenue real do key-values · Vermelho = domain sem monetização</p>
                    </div>
                    <GeoRevenueMatrix start={start} end={end} />
                </div>
            </>}

            {/* ── Aba Funnels ── */}
            {activeTab === 'funnels' && <>
                {(funnelDashQuery.data?.data?.daily_clicks ?? []).length > 0 && (
                    <DailyClicksChart data={funnelDashQuery.data!.data!.daily_clicks} color="#10b981" />
                )}
                {(funnelDashQuery.data?.data?.hourly_clicks ?? []).length > 0 && (
                    <HourlyClicksChart data={funnelDashQuery.data!.data!.hourly_clicks} color="#10b981" />
                )}
                <FunnelsTab
                    funnels={funnels}
                    revenueMap={funnelRevenueMap}
                    revenueLoading={funnelRevenueLoading}
                    isLoading={funnelDashQuery.isLoading}
                    onSelect={setSelectedFunnel}
                />
                <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-6">
                    <div className="mb-4">
                        <h2 className="text-base font-semibold text-gray-900">País × Domain — Funnels</h2>
                        <p className="text-xs text-gray-400 mt-0.5">Revenue real do key-values via utm_content=flow_&#123;id&#125; · Vermelho = domain sem monetização</p>
                    </div>
                    <FunnelGeoRevenueMatrix start={start} end={end} />
                </div>
            </>}

            {/* Grupo selecionado */}
            {selectedGroup && (
                <GroupPanel
                    groupId={selectedGroup.id}
                    groupTitle={selectedGroup.title}
                    groupStatus={selectedGroup.status}
                    start={start}
                    end={end}
                    domainHealthMap={domainHealthMap}
                    allGroups={groups}
                    onClose={handleCloseGroup}
                    onOpenDomain={handleOpenDomainFromGroup}
                />
            )}

            {/* Domain selecionado */}
            {selectedDomain && (
                <DomainPanel
                    domain={selectedDomain}
                    domainHealth={domainHealthMap[selectedDomain]}
                    allGroups={groups}
                    groupRevenueMap={domainGroupRevMap}
                    onClose={handleCloseDomain}
                    onOpenGroup={handleOpenGroupFromDomain}
                />
            )}

            {/* Funnel selecionado */}
            {selectedFunnel && (
                <FunnelPanel
                    funnelId={selectedFunnel.id}
                    funnelTitle={selectedFunnel.title}
                    start={start}
                    end={end}
                    revenueData={(funnelRevenueQuery.data?.data ?? []).find(r => r.funnel_id === selectedFunnel.id)}
                    onClose={() => setSelectedFunnel(null)}
                />
            )}
        </div>
    );
}
